import { MaterialSymbol } from '@florencelabs/icons';
import { today } from '@internationalized/date';
import clsx from 'clsx';
import { ForwardedRef, forwardRef, ReactElement } from 'react';
import {
  Button,
  Calendar as AriaCalendar,
  CalendarCell as AriaCalendarCell,
  CalendarGrid as AriaCalendarGrid,
  RangeCalendar as AriaRangeCalendar,
  Heading,
  CalendarGridHeader,
  CalendarHeaderCell,
  CalendarGridBody,
  DateValue,
  CalendarProps,
  HeadingProps,
  ButtonProps,
  CalendarGridProps,
  CalendarCellProps,
  RangeCalendarProps,
} from 'react-aria-components';
import { tv } from 'tailwind-variants';
import { useTimeZone } from './use-time-zone';

const CalendarHeading = ({
  className,
  ...props
}: HeadingProps): ReactElement => (
  <Heading
    data-testid={'calendar-heading'}
    className={clsx(
      'text-body-medium-semibold first-letter:uppercase',
      className,
    )}
    {...props}
  />
);

interface CalendarHeadingButtonProps extends ButtonProps {
  slot: 'previous' | 'next';
}
const CalendarHeadingButton = ({
  className,
  slot,
  ...props
}: CalendarHeadingButtonProps): ReactElement => {
  return (
    <Button
      data-testid={`calendar-${slot}-btn`}
      className={clsx(
        'h-6 w-6 disabled:cursor-not-allowed disabled:text-gray-950/30',
        className,
      )}
      slot={slot}
      {...props}
    >
      <MaterialSymbol
        name={slot === 'previous' ? 'chevron_left' : 'chevron_right'}
      />
    </Button>
  );
};

const CalendarGrid = ({
  className,
  weekdayStyle = 'short',
  ...props
}: CalendarGridProps): ReactElement => (
  <AriaCalendarGrid
    data-testid={'calendar-grid'}
    className={clsx(
      'w-full border-separate mobile:border-spacing-y-5 mobile:px-3 tablet:border-spacing-y-4 tablet:px-0',
      className,
    )}
    weekdayStyle={weekdayStyle}
    {...props}
  />
);

const CalendarCell = ({
  date,
  timeZone,
  className,
  ...props
}: CalendarCellProps & { timeZone?: string }): ReactElement => (
  <AriaCalendarCell
    data-testid={`calendar-cell-${date.toString()}`}
    className={({
      isFocusVisible,
      isDisabled,
      isOutsideMonth,
      isUnavailable,
    }) =>
      clsx(
        'mx-auto h-9 w-9 overflow-hidden rounded-sm',
        'flex items-center justify-center text-body-medium-medium',
        'selected:bg-primary-200',
        isFocusVisible ? 'outline-2' : 'outline-0',
        !!timeZone &&
          date.toString() === today(timeZone).toString() && [
            "relative after:content-['']",
            'after:absolute after:bottom-1 after:left-1/2 after:-translate-x-1/2',
            'after:block after:h-1 after:w-1 after:rounded-full after:bg-black',
          ],
        isDisabled || isOutsideMonth || isUnavailable
          ? 'cursor-default text-gray-300'
          : '',
        className,
      )
    }
    date={date}
    {...props}
  >
    {({ formattedDate }) => <span>{formattedDate}</span>}
  </AriaCalendarCell>
);

const Calendar = forwardRef(
  <T extends DateValue>(
    { className, children, ...props }: CalendarProps<T>,
    ref: ForwardedRef<HTMLDivElement>,
  ): ReactElement => {
    const timeZone = useTimeZone();
    return (
      <AriaCalendar
        data-testid={'calendar'}
        className={className}
        {...props}
        ref={ref}
      >
        {children ?? (
          <>
            <div className={'mb-6 flex items-center justify-between px-4'}>
              <CalendarHeading />
              <div className={'flex items-center justify-center gap-x-6'}>
                <CalendarHeadingButton slot={'previous'} />
                <CalendarHeadingButton slot={'next'} />
              </div>
            </div>
            <CalendarGrid>
              <CalendarGridHeader>
                {(day) => (
                  <CalendarHeaderCell className={'text-body-medium-medium'}>
                    {day}
                  </CalendarHeaderCell>
                )}
              </CalendarGridHeader>
              <CalendarGridBody>
                {(date) => <CalendarCell date={date} timeZone={timeZone} />}
              </CalendarGridBody>
            </CalendarGrid>
          </>
        )}
      </AriaCalendar>
    );
  },
);

const rangeCalendarCell = tv({
  base: 'flex h-full w-full items-center justify-center text-body-medium-medium',
  variants: {
    selectionState: {
      none: '',
      middle: 'bg-primary-100',
      cap: 'bg-primary-200 rounded-sm',
    },
    isFocusVisible: {
      true: 'outline-2',
      false: 'outline-0',
    },
    isDisabled: { true: 'text-gray-300' },
  },
});

const RangeCalendarCell = ({
  date,
  timeZone,
  className,
  ...props
}: CalendarCellProps & { timeZone?: string }): ReactElement => (
  <AriaCalendarCell
    data-testid={`range-calendar-cell-${date.toString()}`}
    className={({ isFocusVisible }) =>
      clsx(
        'mx-auto h-9 w-9',
        'selected:bg-primary-100 selection-start:rounded-s-sm selection-end:rounded-e-sm',
        !!timeZone &&
          date.toString() === today(timeZone).toString() && [
            "relative after:content-['']",
            'after:absolute after:bottom-1 after:left-1/2 after:-translate-x-1/2',
            'after:block after:h-1 after:w-1 after:rounded-full after:bg-black',
          ],
        isFocusVisible ? 'outline-2' : 'outline-0',
        className,
      )
    }
    date={date}
    {...props}
  >
    {({
      isDisabled,
      isOutsideMonth,
      isUnavailable,
      isSelected,
      isSelectionStart,
      isSelectionEnd,
      formattedDate,
      isFocusVisible,
    }) => (
      <span
        className={rangeCalendarCell({
          selectionState:
            isSelected && (isSelectionStart || isSelectionEnd)
              ? 'cap'
              : isSelected
                ? 'middle'
                : 'none',
          isFocusVisible,
          isDisabled: isDisabled || isOutsideMonth || isUnavailable,
        })}
      >
        {formattedDate}
      </span>
    )}
  </AriaCalendarCell>
);

const RangeCalendar = <T extends DateValue>({
  className,
  children,
  ...props
}: RangeCalendarProps<T>): ReactElement => {
  const timeZone = useTimeZone();
  return (
    <AriaRangeCalendar
      data-testid={'range-calendar'}
      className={clsx('w-fit', className)}
      {...props}
    >
      {children ?? (
        <>
          <div className={'mb-2 flex items-center justify-between'}>
            <CalendarHeading />
            <div
              className={
                'flex items-center justify-center mobile:gap-x-2 tablet:gap-x-6'
              }
            >
              <CalendarHeadingButton slot={'previous'} />
              <CalendarHeadingButton slot={'next'} />
            </div>
          </div>
          <AriaCalendarGrid
            className={clsx(
              'w-full border-separate border-spacing-x-0 mobile:border-spacing-y-5 tablet:border-spacing-y-4 [&_td]:px-0',
              className,
            )}
            weekdayStyle={'short'}
          >
            <CalendarGridHeader>
              {(day) => (
                <CalendarHeaderCell
                  className={'text-center text-body-medium-medium'}
                >
                  {day}
                </CalendarHeaderCell>
              )}
            </CalendarGridHeader>
            <CalendarGridBody>
              {(date) => <RangeCalendarCell date={date} timeZone={timeZone} />}
            </CalendarGridBody>
          </AriaCalendarGrid>
        </>
      )}
    </AriaRangeCalendar>
  );
};
Calendar.displayName = 'Calendar';

export {
  // Types
  type RangeCalendarProps,
  type CalendarProps,
  // Components
  Calendar,
  RangeCalendar,
  CalendarHeading,
  CalendarHeadingButton,
  CalendarGrid,
  CalendarGridHeader,
  CalendarHeaderCell,
  CalendarGridBody,
  CalendarCell,
  RangeCalendarCell,
};
